import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import useShowTravelTips from 'hooks/useShowTravelTips';
import recomendationsPersonImage from 'images/brands-unique/cds/recomendations-person.png';
import iconLightbulb from 'images/icons/icon-lightbulb.png';
import iconScarf from 'images/icons/icon-scarf.png';
import iconChips from 'images/icons/icon-chips.png';

const ICON_CIRCLE_CLASS =
  'flex-shrink-0 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.12)]';

const TIPS_CONFIG = [
  {
    icon: iconScarf,
    subtitleKey: 'text.travel_tips_cold_subtitle',
    bodyKey: 'text.travel_tips_cold_body',
  },
  {
    icon: iconChips,
    subtitleKey: 'text.travel_tips_snack_subtitle',
    bodyKey: 'text.travel_tips_snack_body',
  },
];

/**
 * Renders a single travel tip: icon in circle + subtitle and body text.
 * @param {object} props - icon, subtitle, body.
 * @param {string} props.icon - Image source for the tip icon.
 * @param {string} props.subtitle - Subtitle text.
 * @param {string} props.body - Body text.
 */
function TravelTipItem({ icon, subtitle, body }) {
  return (
    <div className="flex gap-3">
      <div className={ICON_CIRCLE_CLASS}>
        <img src={icon} alt="" className="w-6 h-6 object-contain" aria-hidden="true" />
      </div>
      <div className="space-y-1.5 min-w-0">
        <Text color="primary" weight="semibold" size="M">
          {subtitle}
        </Text>
        <Text size="S" color="grayMedium" className="leading-relaxed">
          {body}
        </Text>
      </div>
    </div>
  );
}

TravelTipItem.propTypes = {
  icon: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
};

/**
 * TravelTipsMessage component. Shows travel tips on purchase complete.
 * Visibility: useShowTravelTips (SHOW_TRAVEL_TIPS_PURCHASE_COMPLETE + env.travelTipsAllowedRoutes + purchase route).
 */
function TravelTipsMessage() {
  const { t } = useTranslation('purchase');
  const showTravelTips = useShowTravelTips();

  if (!showTravelTips) return null;

  return (
    <div className="bg-white rounded-3xl shadow-[0_10px_25px_rgba(0,0,0,0.1)] border border-gray-100 overflow-hidden flex flex-col md:flex-row">
      <div className="flex-none w-full aspect-[2/1] max-h-[160px] md:max-h-none md:w-40 md:aspect-auto md:h-auto md:min-h-[160px] md:self-stretch shrink-0">
        <img
          src={recomendationsPersonImage}
          alt=""
          className="w-full h-full object-cover object-[28%_50%] rounded-t-2xl md:rounded-t-none md:rounded-l-2xl"
          aria-hidden="true"
        />
      </div>
      <div className="flex-1 p-3 md:p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <img
            src={iconLightbulb}
            alt=""
            className="w-6 h-6 flex-shrink-0 object-contain"
            aria-hidden="true"
          />
          <Text color="primary" weight="bold" size="L">
            {t('text.travel_tips_title')}
          </Text>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {TIPS_CONFIG.map(({ icon, subtitleKey, bodyKey }) => (
            <TravelTipItem
              key={subtitleKey}
              icon={icon}
              subtitle={t(subtitleKey)}
              body={t(bodyKey)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default TravelTipsMessage;
